/**
 * Identifies the type of comment/remark (which determines where the remark should be displayed)
 */

export enum CommentType {
  General,
  Attendance,
  Facility,
  Runway,
  RunwayEnd,
  Tower,
  Communications,
  Services,
  TravelInfo,
  Ultralights,
  Member,
  OwnerMgr,
  Aircraft,
  Charts,
  Geographic,
  Lighting,
  MilitaryUse,
  PatternAlt,
  Gliders,
  Customs,
  Miscellaneous,
  Admin
}


/**
 * Data model for airport comments/remarks (including FAA remarks, as well as comments left by business or airport
 * manager).
 */
export class AirportComment {
  /**
   * Unique identifier for this record in the database
   */
  id: number;

  /**
   * Airport record ID for which this record applies
   */
  airportId: number;

  /**
   * Source ID that identifies the source of this data (e.g., airport manager)
   */
  sourceId: number;

  /**
   * Comment/remark type
   */
  commentType: CommentType;

  /**
   * Data element or category this comment or remark pertains to. This varies based on commentType:
   *  - General: This field will be one of the following:
   *      * null for general FAA remarks
   *      * "Notes" for general remarks/comments left by the airport manager.
   *  - Attendance: This field will always be the string "Airport"
   *  - Runway: This field will contain the landing surface name (e.g., "02/20")
   *  - RunwayEnd: This field will contain the takeoff/landing area (e.g., "32" or "H1")
   *  - Tower: This field will pretty much always be the string "Schedule", and the remark text will usually specify
   *      tower hours of operation.
   * In all other cases, the CommentRef will be null.
   */
  commentRef: string;

  /**
   * writer of comment
   */
  recid?: number;

  /**
   * Remark/comment text
   */
  text: string;

  /**
   * Date of comment
   */

  commentDate?: Date;

  /**
   * Parses airport comment
   * @param data {any} Comment data as returned by API
   * @return {AirportComment} Parsed airport comment
   */
  static parse(data: any): AirportComment {
    return {
      id: data.commentId,
      airportId: data.airportId,
      sourceId: data.sourceId,
      commentType: this.parseCommentType(data.commentType),
      commentRef: data.commentRef,
      recid: data.recid,
      text: data.text,
      commentDate: data.addDate ? new Date(data.addDate) : null
    };
  }

  /**
   * Parses comment type
   * @param value {any} Comment type as returned by API
   * @return {CommentType} Parsed comment type
   */
  static parseCommentType(value: any): CommentType {
    switch (value) {
      case 'General':
        return CommentType.General;
      case 'Attendance':
        return CommentType.Attendance;
      case 'Facility':
        return CommentType.Facility;
      case 'Runway':
        return CommentType.Runway;
      case 'Runway-End':
        return CommentType.RunwayEnd;
      case 'Tower':
        return CommentType.Tower;
      case 'Communications':
        return CommentType.Communications;
      case 'Services':
        return CommentType.Services;
      case 'TravelInfo':
        return CommentType.TravelInfo;
      case 'Ultralights':
        return CommentType.Ultralights;
      case 'Member':
        return CommentType.Member;
      case 'Owner-Mgr':
        return CommentType.OwnerMgr;
      case 'Aircraft':
        return CommentType.Aircraft;
      case 'Charts':
        return CommentType.Charts;
      case 'Geographic':
        return CommentType.Geographic;
      case 'Lighting':
        return CommentType.Lighting;
      case 'Military Use':
        return CommentType.MilitaryUse;
      case 'PatternAlt':
        return CommentType.PatternAlt;
      case 'Gliders':
        return CommentType.Gliders;
      case 'Customs':
        return CommentType.Customs;
      case 'Miscellaneous':
        return CommentType.Miscellaneous;
      case 'Admin':
        return CommentType.Admin;
      default:
        return null;
    }
  }

  /**
   * Formats comment type for display
   * @param value {CommentType} CommentType to be formatted as string
   * @return {string} Formatted CommentType
   */
  static formatCommentType(value: CommentType): string {
    switch (value) {
      case CommentType.General:
        return 'General';
      case CommentType.Attendance:
        return 'Attendance';
      case CommentType.Facility:
        return 'Facility';
      case CommentType.Runway:
        return 'Runway';
      case CommentType.RunwayEnd:
        return 'Runway-End';
      case CommentType.Tower:
        return 'Tower';
      case CommentType.Communications:
        return 'Communications';
      case CommentType.Services:
        return 'Services';
      case CommentType.TravelInfo:
        return 'TravelInfo';
      case CommentType.Ultralights:
        return 'Ultralights';
      case CommentType.Member:
        return 'Member';
      case CommentType.OwnerMgr:
        return 'Owner-Mgr';
      case CommentType.Aircraft:
        return 'Aircraft';
      case CommentType.Charts:
        return 'Charts';
      case CommentType.Geographic:
        return 'Geographic';
      case CommentType.Lighting:
        return 'Lighting';
      case CommentType.MilitaryUse:
        return 'Military Use';
      case CommentType.PatternAlt:
        return 'PatternAlt';
      case CommentType.Gliders:
        return 'Gliders';
      case CommentType.Customs:
        return 'Customs';
      case CommentType.Miscellaneous:
        return 'Miscellaneous';
      case CommentType.Admin:
        return 'Admin';
      default:
        return 'General';
    }
  }
}
